import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiErrorMessage } from '../api/client';
import * as ordersApi from '../api/ordersApi';
import { FifoFormulationCost } from '../types/inventory';
import { PaymentBadge, StatusBadge } from '../components/StatusBadge';
import { Card, ConfirmDialog, ErrorBanner, Skeleton, Spinner } from '../components/ui';
import {
  IconAlert,
  IconArrowLeft,
  IconCheck,
  IconChevronRight,
  IconDownload,
  IconExternal,
  IconTruck,
} from '../components/icons';
import ShipmentModal from '../components/ShipmentModal';
import OilPicker, { OilCodeTag, oilCodeMap, useOilInventory } from '../components/OilPicker';
import { InventoryItem } from '../types/inventory';
import {
  COURIER_DRIVEN_STATUSES,
  CustomFormula,
  FormulaOil,
  hasReachedStage,
  isAwaitingPayment,
  Order,
  ORDER_STATUSES,
  OrderStatus,
  OrderShipment,
  OilTier,
  ShipmentStep,
} from '../types/order';
import {
  capitalize,
  formatDateTime,
  formatINR,
  formatINRPrecise,
  formatMl,
  round1,
  shortId,
} from '../utils/format';

// Sentinel value for the status dropdown's self-ship entry. Deliberately not a
// real OrderStatus: it names an action (skip Shiprocket, deliver by hand), and
// the server reaches 'delivered' through its own guarded endpoint.
const SELF_SHIP_OPTION = '__self_ship' as const;

export default function OrderDetailPage() {
  const { id = '' } = useParams();

  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [notice, setNotice] = useState('');

  // Status update form. The dropdown also offers self-ship, which is NOT a status
  // (it's a fulfilment type) — picking it routes to the self-ship confirmation
  // rather than a status PATCH, so both entry points run the same guarded action.
  const [newStatus, setNewStatus] = useState<OrderStatus | typeof SELF_SHIP_OPTION>('pending');
  const [statusNotes, setStatusNotes] = useState('');
  const [savingStatus, setSavingStatus] = useState(false);

  // Shiprocket shipment creation modal
  const [shipmentOpen, setShipmentOpen] = useState(false);
  const [copied, setCopied] = useState('');
  const [schedulingPickup, setSchedulingPickup] = useState<string | null>(null);
  // Inline pickup-date picker: which shipment's picker is open, and the chosen date.
  const [pickupFor, setPickupFor] = useState<string | null>(null);
  const [pickupDate, setPickupDate] = useState('');
  const [downloadingLabels, setDownloadingLabels] = useState(false);
  // Formulated stage + GST invoice download.
  const [markingFormulated, setMarkingFormulated] = useState(false);
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);
  // Recovering a half-created shipment: which parcel is retrying, and whether a
  // "Sync from Shiprocket" is in flight (order-wide — it syncs every parcel).
  const [retryingShipment, setRetryingShipment] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);

  // Self-ship: delivered by hand, skipping Shiprocket entirely.
  const [confirmSelfShip, setConfirmSelfShip] = useState(false);
  const [selfShipNote, setSelfShipNote] = useState('');
  const [selfShipping, setSelfShipping] = useState(false);

  // Cancel
  const [cancelReason, setCancelReason] = useState('');
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  // Oil-formula editing. One editor is open at a time — either the legacy
  // formulation_details recipe, or a specific modern custom line item.
  const [editTarget, setEditTarget] = useState<
    { kind: 'legacy' } | { kind: 'custom'; itemId: string } | null
  >(null);
  const [oils, setOils] = useState<FormulaOil[]>([]);
  const [savingFormula, setSavingFormula] = useState(false);
  // Live oil list for the name picker in the editors — fetched once from the
  // same GET /api/admin/inventory the Inventory page uses.
  const { oils: inventoryOils } = useOilInventory();
  // Shelf numbers for every oil named on this page — the formula panels, the
  // cost breakdown and the editor rows all identify the same bottles, so they
  // all read from one map rather than each looking the oil up its own way.
  const oilCodes = useMemo(() => oilCodeMap(inventoryOils), [inventoryOils]);

  // Formulation-cost panel: total only until the admin asks for the per-oil rows.
  const [costOpen, setCostOpen] = useState(false);
  // FIFO cost is computed server-side (the cost layers live in the ecommerce
  // service), so it arrives on its own request rather than from the order body.
  const [formulationCost, setFormulationCost] = useState<FifoFormulationCost | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const o = await ordersApi.getOrder(id);
      setOrder(o);
      setNewStatus(o.status);
      setEditTarget(null);
      setOils([]);
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  // Every oil this order compounds — the legacy recipe plus each modern custom
  // line item — summed by name. Only used to decide whether there is anything to
  // cost; the costing itself happens server-side.
  const formulationOils = useMemo(() => {
    const byName = new Map<string, number>();
    const add = (oils: { name: string; ml: number }[] | undefined) =>
      (oils ?? []).forEach((o) => {
        const name = (o.name ?? '').trim();
        if (!name) return;
        byName.set(name, (byName.get(name) ?? 0) + (Number(o.ml) || 0));
      });
    add(order?.formulation_details?.oils);
    (order?.custom_formulas ?? []).forEach((cf) => add(cf.oils));
    return Array.from(byName, ([name, ml]) => ({ name, ml }));
  }, [order]);

  // FIFO costing, re-fetched whenever the formula changes — an admin editing the
  // ml must see the cost move with it. Best-effort: the panel simply stays hidden
  // if costing is unavailable, rather than blocking the rest of the order view.
  useEffect(() => {
    if (!id || formulationOils.length === 0) {
      setFormulationCost(null);
      return;
    }
    let stale = false;
    ordersApi
      .getOrderCost(id)
      .then((c) => { if (!stale) setFormulationCost(c); })
      .catch(() => { if (!stale) setFormulationCost(null); });
    return () => { stale = true; };
  }, [id, formulationOils]);

  // Packaging the order consumes, folded into the cost response server-side.
  // Absent on older responses and when the packaging service is unreachable —
  // the panel then just shows the oil cost rather than an empty section.
  const packaging = formulationCost?.packaging ?? null;

  const flash = (msg: string) => {
    setNotice(msg);
    setActionError('');
    window.setTimeout(() => setNotice(''), 4000);
  };

  const handleUpdateStatus = async () => {
    if (!order) return;
    // Self-ship isn't a status transition — hand it to its own confirmation.
    if (newStatus === SELF_SHIP_OPTION) {
      setConfirmSelfShip(true);
      return;
    }
    setSavingStatus(true);
    setActionError('');
    try {
      await ordersApi.updateStatus(order.id, newStatus, statusNotes.trim());
      setStatusNotes('');
      flash('Status updated');
      await load();
    } catch (err) {
      setActionError(apiErrorMessage(err));
    } finally {
      setSavingStatus(false);
    }
  };

  /**
   * "Mark as Formulated" — the perfume is compounded and bottled. Moves the
   * order to 'formulated' and registers it with Shiprocket (order record only:
   * no courier, no AWB, no label, no customer notification).
   *
   * The status is saved even when Shiprocket fails, so the outcome is reported
   * in two parts rather than as a flat success — the bottle is made either way,
   * and the admin needs to know whether the courier side actually happened.
   */
  const handleMarkFormulated = async () => {
    if (!order) return;
    setMarkingFormulated(true);
    setActionError('');
    try {
      const res = await ordersApi.markFormulated(order.id);
      if (res.shipment_error) {
        // Deliberately NOT a success toast: half of this worked.
        setActionError(
          `Order marked as Formulated. Note: Shiprocket order creation failed — ${res.shipment_error}. ` +
            `This will be retried when you click Ready to Ship.`
        );
        setNotice('');
      } else {
        flash('Order marked as Formulated. Shiprocket order created.');
      }
      await load();
    } catch (err) {
      setActionError(apiErrorMessage(err));
    } finally {
      setMarkingFormulated(false);
    }
  };

  /**
   * "Self-ship" — the order is being delivered by hand. Skips Shiprocket entirely
   * and moves straight to Delivered; the customer gets the delivered WhatsApp.
   * Stock and coupon usage were already settled at payment, so nothing else runs.
   */
  const handleSelfShip = async () => {
    if (!order) return;
    setSelfShipping(true);
    setActionError('');
    try {
      await ordersApi.selfShipOrder(order.id, selfShipNote.trim() || undefined);
      setConfirmSelfShip(false);
      setSelfShipNote('');
      flash('Order marked as self-shipped and delivered.');
      await load();
    } catch (err) {
      setActionError(apiErrorMessage(err));
      setConfirmSelfShip(false);
    } finally {
      setSelfShipping(false);
    }
  };

  // Download this order's 4×6 GST invoice. Available for any paid order, at any
  // stage from Formulated onwards.
  const handleDownloadInvoice = async () => {
    if (!order) return;
    setDownloadingInvoice(true);
    setActionError('');
    try {
      await ordersApi.downloadInvoice(order.id);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : apiErrorMessage(err));
    } finally {
      setDownloadingInvoice(false);
    }
  };

  // Copy an AWB or a Shiprocket order id — whatever the admin needs to paste into
  // the Shiprocket dashboard. `copied` holds the value so only that button ticks.
  const handleCopy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(value);
      window.setTimeout(() => setCopied(''), 2000);
    } catch {
      /* clipboard unavailable — no-op */
    }
  };

  const handleSchedulePickup = async (shipmentId: string, date?: string) => {
    if (!order) return;
    setSchedulingPickup(shipmentId);
    setActionError('');
    try {
      const res = await ordersApi.schedulePickup(order.id, shipmentId, date || null);
      flash(
        res.pickup_scheduled_date
          ? `Pickup scheduled for ${res.pickup_scheduled_date}`
          : 'Pickup scheduled'
      );
      setPickupFor(null);
      await load();
    } catch (err) {
      setActionError(apiErrorMessage(err));
    } finally {
      setSchedulingPickup(null);
    }
  };

  /**
   * Resume a half-created shipment. Hits the same POST /ship endpoint as the
   * initial send — it's resumable server-side, so it picks up from whichever step
   * failed and never re-buys an AWB that already exists. Safe to press twice.
   */
  const handleRetryShipment = async (shipmentId: string) => {
    if (!order) return;
    setRetryingShipment(shipmentId);
    setActionError('');
    try {
      const res = await ordersApi.createShipment(order.id, null, undefined, shipmentId);
      flash(
        res.shipment_step === 'complete'
          ? `Shipment complete — AWB ${res.awb_number}`
          : `Picked up where it stopped. Now at: ${STEP_LABELS[res.shipment_step ?? 'failed']}`
      );
      await load();
    } catch (err) {
      setActionError(apiErrorMessage(err));
    } finally {
      setRetryingShipment(null);
    }
  };

  /**
   * Pull the current Shiprocket state back into our DB — the "I fixed it in the
   * Shiprocket dashboard" path. Reports what it actually found, so an unchanged
   * sync says so rather than implying it repaired something.
   */
  const handleSyncShipment = async () => {
    if (!order) return;
    setSyncing(true);
    setActionError('');
    try {
      const res = await ordersApi.syncShipment(order.id);
      if (res.changes_made.length === 0) {
        flash('Nothing changed — Shiprocket shows the same state.');
      } else {
        const found = [
          res.awb_number ? `AWB ${res.awb_number}` : null,
          res.courier_name,
          res.label_url ? 'Label URL available' : null,
        ]
          .filter(Boolean)
          .join(', ');
        flash(`Synced. Found: ${found || res.changes_made.join(', ')}.`);
      }
      await load();
    } catch (err) {
      setActionError(apiErrorMessage(err));
    } finally {
      setSyncing(false);
    }
  };

  // Combined PDF of every AWB'd shipment on this order (one download for a split order).
  const handleDownloadAllLabels = async () => {
    if (!order) return;
    const ids = (order.shipments ?? [])
      .filter((s) => s.awb_number && s.shiprocket_shipment_id)
      .map((s) => s.shiprocket_shipment_id as string);
    if (ids.length === 0) return;
    setDownloadingLabels(true);
    setActionError('');
    try {
      const { label_url } = await ordersApi.bulkLabels(ids);
      if (label_url) window.open(label_url, '_blank', 'noopener');
      else setActionError('No combined label URL was returned by Shiprocket.');
    } catch (err) {
      setActionError(apiErrorMessage(err));
    } finally {
      setDownloadingLabels(false);
    }
  };

  const handleCancel = async () => {
    if (!order) return;
    setCancelling(true);
    setActionError('');
    try {
      await ordersApi.cancelOrder(order.id, cancelReason.trim());
      setConfirmCancel(false);
      setCancelReason('');
      flash('Order cancelled');
      await load();
    } catch (err) {
      setActionError(apiErrorMessage(err));
      setConfirmCancel(false);
    } finally {
      setCancelling(false);
    }
  };

  // Legacy formulation_details recipe (tier-aware).
  const startEditLegacy = () => {
    setOils(
      (order?.formulation_details?.oils ?? []).map((o) => ({
        ...o,
        ml: round1(o.ml),
        tier: o.tier ?? '',
      }))
    );
    setEditTarget({ kind: 'legacy' });
    setActionError('');
  };

  // Modern custom line item (tier-aware — oils carry their note).
  const startEditCustom = (cf: CustomFormula) => {
    setOils(cf.oils.map((o) => ({ name: o.name, ml: round1(o.ml), tier: o.tier ?? '' })));
    setEditTarget({ kind: 'custom', itemId: cf.order_item_id });
    setActionError('');
  };

  const cancelEditFormula = () => {
    setEditTarget(null);
    setOils([]);
  };

  const updateOil = (i: number, patch: Partial<FormulaOil>) => {
    setOils((prev) => prev.map((o, idx) => (idx === i ? { ...o, ...patch } : o)));
  };

  const removeOil = (i: number) => setOils((prev) => prev.filter((_, idx) => idx !== i));

  // Legacy respects its stored style (flat vs tiered); custom is always tiered.
  const editorTiered =
    editTarget?.kind === 'legacy'
      ? order?.formulation_details?.formula_style !== 'flat'
      : editTarget?.kind === 'custom';

  const addOil = () =>
    setOils((prev) => [...prev, { name: '', ml: 0, tier: editorTiered ? 'heart' : '' }]);

  const formulaValid = oils.length > 0 && oils.every((o) => o.name.trim() && Number(o.ml) > 0);

  const handleSaveFormula = async () => {
    if (!order || !editTarget || !formulaValid) return;
    setSavingFormula(true);
    setActionError('');
    try {
      const payload: FormulaOil[] = oils.map((o) => ({
        name: o.name.trim(),
        ml: round1(o.ml),
        tier: o.tier || '',
      }));

      const result =
        editTarget.kind === 'legacy'
          ? await ordersApi.updateFormulation(order.id, payload)
          : await ordersApi.updateCustomFormula(order.id, editTarget.itemId, payload);

      setEditTarget(null);
      setOils([]);
      // Always say what happened to stock, including when the answer is
      // "nothing". An edit that silently moved 0.4 ml of Sandalwood — or
      // silently didn't — is how stock and formulas drift apart unnoticed.
      flash(['Formula saved.', result?.inventory?.message].filter(Boolean).join(' '));
      await load();
    } catch (err) {
      // A 409 here means the save was REFUSED and nothing changed — the message
      // from the server names the oil that blocked it.
      setActionError(apiErrorMessage(err));
    } finally {
      setSavingFormula(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-28" />
        <div className="grid gap-4 lg:grid-cols-2">
          <Skeleton className="h-48" />
          <Skeleton className="h-48" />
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="space-y-4">
        <Link to="/orders" className="btn-ghost -ml-3">
          <IconArrowLeft className="h-4 w-4" /> Back to orders
        </Link>
        <ErrorBanner message={error || 'Order not found'} onRetry={load} />
      </div>
    );
  }

  const addr = order.shipping_address;
  const canCancel = !['delivered', 'completed', 'cancelled', 'refunded'].includes(order.status);
  const awaitingPayment = isAwaitingPayment(order);
  // Payment confirmation flows through the payment endpoints (verify/webhook/
  // reconciliation) — manually forcing 'confirmed' on an unpaid online order
  // would recreate the ghost-order bug this system was fixed to prevent.
  const paymentReceived = order.payment_method === 'cod' || order.payment_status === 'paid';

  // Oil recipes stay hidden until the order reaches 'processing' (paid and in
  // production), then remain visible for the rest of its life. Gates BOTH recipe
  // surfaces — the legacy formulation_details panel and the modern per-line-item
  // custom_formulas panel — plus the cost breakdown, which lists the same oils
  // and ml and would otherwise leak the formula around them.
  const showFormula = hasReachedStage(order.status, 'processing');

  // Shiprocket shipping state. An order can hold several shipments (partial
  // fulfilment). The order-level rollup lives in order.shipping_status; per-parcel
  // detail is in order.shipments, and remaining_items is what's still to ship.
  const shippingStatus = order.shipping_status;
  const shipments = order.shipments ?? [];
  const remainingToShip = (order.remaining_items ?? []).reduce((s, r) => s + r.remaining, 0);
  const isRto = shippingStatus === 'rto_initiated' || shippingStatus === 'rto_delivered';
  const isDelivered = shippingStatus === 'delivered';
  // A "Create shipment" action is offered while there are still units to ship,
  // payment is in, and the order isn't cancelled/refunded.
  // A parcel stuck mid-creation owns the next action — its own Retry resumes it.
  // Offering "Create Another Shipment" beside that would be a second button doing
  // the same thing (the ship endpoint resumes) under a name that says otherwise.
  //
  // A parcel sitting at 'order_created' on a FORMULATED order is the exception:
  // that is exactly where "Mark as Formulated" is supposed to leave it, not a
  // failure. Treating it as unresolved would hide "Mark Ready to Ship" behind a
  // Retry button and make the intended flow look broken.
  const parkedAtFormulated = (sh: OrderShipment) =>
    order.status === 'formulated' && shipmentState(sh) === 'order_created' && !sh.shipment_error;

  const hasUnresolvedShipment = shipments.some((sh) => {
    const step = shipmentState(sh);
    return step !== 'complete' && step !== 'label_generated' && !parkedAtFormulated(sh);
  });
  const canShip =
    remainingToShip > 0 &&
    paymentReceived &&
    !hasUnresolvedShipment &&
    order.fulfillment_type !== 'self_ship' && // delivered by hand — no courier ever
    !['cancelled', 'refunded'].includes(order.status);

  // ── Formulated stage ───────────────────────────────────────────────────────
  // The bench milestone between "paid and in production" and "handed to a
  // courier". Offered only from the two stages that precede it.
  const isFormulated = order.status === 'formulated';
  const canMarkFormulated = paymentReceived && ['confirmed', 'processing'].includes(order.status);
  // A GST invoice is only issued against money actually received — COD orders
  // stay pending until collection, so paymentReceived is deliberately NOT the
  // test here.
  const canDownloadInvoice = order.payment_status === 'paid';
  // Formulated but no Shiprocket order: creating it failed, and Ready to Ship
  // will create it before assigning a courier.
  const shiprocketOrderId =
    order.shiprocket_order_id ?? shipments.find((s) => s.shiprocket_order_id)?.shiprocket_order_id ?? null;

  // ── Self-ship (exception path) ─────────────────────────────────────────────
  // Paid, never registered with Shiprocket, and not yet finished. Mirrors the
  // server guards, which also check the shipments table — hence shiprocketOrderId
  // rather than order.shiprocket_order_id alone.
  const isSelfShipped = order.fulfillment_type === 'self_ship';
  const canSelfShip =
    order.payment_status === 'paid' &&
    !shiprocketOrderId &&
    !['delivered', 'completed', 'cancelled', 'refunded'].includes(order.status);

  return (
    <div className="space-y-6">
      <Link to="/orders" className="btn-ghost -ml-3">
        <IconArrowLeft className="h-4 w-4" /> Back to orders
      </Link>

      {notice && (
        <div className="rounded-xl border border-ok/40 bg-ok/10 px-4 py-3 text-sm text-ok">{notice}</div>
      )}
      {actionError && <ErrorBanner message={actionError} />}

      {awaitingPayment && (
        <div className="rounded-xl border border-warn/40 bg-warn/10 px-4 py-3 text-sm text-warn">
          <span className="font-semibold">Awaiting payment.</span> The customer is still at the
          payment gateway — this is not an actionable order yet. It will confirm automatically when
          payment succeeds, or be auto-cancelled by the system if the payment session expires.
        </div>
      )}

      {/* Order summary */}
      <div className="card p-5">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-lg font-semibold">Order {shortId(order.id)}</h1>
            <p className="mt-0.5 text-sm text-sub">
              Placed {formatDateTime(order.created_at)} ·{' '}
              {order.order_type === 'formulation' ? 'Custom formulation' : 'E-commerce'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge
              status={order.status}
              label={awaitingPayment ? 'Awaiting payment' : undefined}
            />
            <PaymentBadge status={order.payment_status} />
          </div>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Customer */}
        <Card title="Customer">
          {addr ? (
            <div className="space-y-1 text-sm">
              <p className="font-medium">{addr.full_name}</p>
              <p className="text-sub">{addr.phone}</p>
              <p className="text-sub">
                {[addr.address_line1, addr.address_line2].filter(Boolean).join(', ')}
              </p>
              <p className="text-sub">
                {addr.city}, {addr.state} — {addr.pincode}
              </p>
            </div>
          ) : (
            <p className="text-sm text-sub">No shipping address on file</p>
          )}
        </Card>

        {/* Payment */}
        <Card title="Payment">
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between gap-3">
              <dt className="text-sub">Method</dt>
              <dd className="font-medium">{order.payment?.method ?? order.payment_method ?? '—'}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-sub">Amount</dt>
              <dd className="font-medium">{formatINR(order.payment?.amount ?? order.total)}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-sub">Cashfree order ID</dt>
              <dd className="break-all text-right font-medium">
                {order.payment?.cashfree_order_id ?? '—'}
              </dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-sub">Status</dt>
              <dd>
                <PaymentBadge status={order.payment?.status ?? order.payment_status} />
              </dd>
            </div>
            {order.coupon_code && (
              <div className="flex justify-between gap-3">
                <dt className="text-sub">Coupon</dt>
                <dd className="font-medium">{order.coupon_code}</dd>
              </div>
            )}
          </dl>
        </Card>
      </div>

      {/* Status management — kept high on the page: reading and changing the
          status is the most common reason an admin opens an order. */}
      <Card title="Status management">
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-sub">Current status:</span>
            <StatusBadge status={order.status} />
          </div>
          <div>
            <label className="label">New status</label>
            <select
              className="input"
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value as OrderStatus | typeof SELF_SHIP_OPTION)}
            >
              {ORDER_STATUSES.map((s) => {
                const courierDriven = COURIER_DRIVEN_STATUSES.includes(s);
                const needsPayment = s === 'confirmed' && !paymentReceived;
                return (
                  <option key={s} value={s} disabled={needsPayment || courierDriven}>
                    {capitalize(s.replace(/_/g, ' '))}
                    {needsPayment ? ' (requires payment)' : ''}
                    {courierDriven ? ' (via Shiprocket)' : ''}
                  </option>
                );
              })}
              {/* Delivered is courier-driven and therefore disabled above. This is
                  the one way to reach it by hand, and it carries the self-ship
                  consequences with it — so it says so rather than reading as a
                  plain status. */}
              {canSelfShip && (
                <option value={SELF_SHIP_OPTION}>Delivered — self-ship (skip Shiprocket)</option>
              )}
            </select>
            {!paymentReceived && (
              <p className="mt-1.5 text-xs text-sub">
                Payment hasn't been received — orders confirm automatically when payment
                succeeds. Manually confirming an unpaid online order is disabled.
              </p>
            )}
          </div>
          <div>
            <label className="label">Notes</label>
            <textarea
              className="input min-h-[72px]"
              placeholder="Optional notes for the status change…"
              value={statusNotes}
              onChange={(e) => setStatusNotes(e.target.value)}
            />
          </div>
          <button
            className="btn-gold"
            onClick={handleUpdateStatus}
            disabled={savingStatus || newStatus === order.status}
          >
            {savingStatus && <Spinner />}
            Update status
          </button>
        </div>
      </Card>

      {/* Items */}
      <Card title="Order items" bodyClassName="!p-0">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-edge">
                <th className="th">Product</th>
                <th className="th">Qty</th>
                <th className="th">Unit price (incl. GST)</th>
                <th className="th">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-edge">
              {(order.items ?? []).map((item) => (
                <tr key={item.id}>
                  <td className="td font-medium">{item.product_name}</td>
                  <td className="td text-sub">{item.quantity}</td>
                  <td className="td text-sub">{formatINR(item.unit_price)}</td>
                  <td className="td font-medium">{formatINR(item.total_price)}</td>
                </tr>
              ))}
            </tbody>
            {/* Prices are GST-inclusive: `total` is what the customer paid,
                and `subtotal` + `tax_amount` is its statutory split. The tax is
                one IGST line at 18%, matching the GST invoice PDF — never a
                CGST+SGST pair. */}
            <tfoot>
              {order.discount_amount > 0 && (
                <>
                  <tr className="border-t border-edge">
                    <td colSpan={3} className="td text-right text-sub">
                      Price (incl. GST)
                    </td>
                    <td className="td">{formatINR(order.total + order.discount_amount)}</td>
                  </tr>
                  <tr>
                    <td colSpan={3} className="td text-right text-sub">
                      Discount{order.coupon_code ? ` (${order.coupon_code})` : ''}
                    </td>
                    <td className="td text-ok">−{formatINR(order.discount_amount)}</td>
                  </tr>
                </>
              )}
              <tr className={order.discount_amount > 0 ? '' : 'border-t border-edge'}>
                <td colSpan={3} className="td text-right text-sub">
                  Subtotal (excl. GST)
                </td>
                <td className="td">{formatINR(order.subtotal)}</td>
              </tr>
              <tr>
                <td colSpan={3} className="td text-right text-sub">
                  IGST (18%)
                </td>
                <td className="td">{formatINR(order.tax_amount)}</td>
              </tr>
              <tr className="border-t border-edge">
                <td colSpan={3} className="td text-right font-semibold">
                  Total (incl. GST)
                </td>
                <td className="td font-semibold">{formatINR(order.total)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </Card>

      {/* Formulation — admins compound from (and may correct) the oil formula.
          Hidden until the order reaches 'processing' (i.e. paid and in
          production), then stays visible for the rest of its life so shipped and
          completed orders remain auditable. Cancelled/refunded orders never
          show it — they may have been killed before production ever started. */}
      {order.order_type === 'formulation' && order.formulation_details && showFormula && (
        <Card
          title="Formulation"
          action={
            editTarget?.kind !== 'legacy' && (
              <button className="btn-ghost text-sm" onClick={startEditLegacy}>
                Edit formula
              </button>
            )
          }
        >
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between gap-3">
              <dt className="text-sub">Bottle size</dt>
              <dd className="font-medium">{order.formulation_details.bottle_size_ml} ml</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-sub">Cluster</dt>
              <dd className="font-medium">
                {order.formulation_details.personality_data?.cluster_name ??
                  order.formulation_details.personality_data?.cluster ??
                  '—'}
              </dd>
            </div>
            <div>
              <dt className="mb-1 text-sub">Personality summary</dt>
              <dd className="whitespace-pre-wrap">{order.formulation_details.personality_summary}</dd>
            </div>
          </dl>

          {/* Oil formula */}
          <div className="mt-5 border-t border-edge pt-4">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-sm font-semibold">Oil formula</h3>
              {editTarget?.kind !== 'legacy' && (
                <span className="text-xs text-sub">
                  Total{' '}
                  {formatMl(
                    (order.formulation_details.oils ?? []).reduce((s, o) => s + (Number(o.ml) || 0), 0)
                  )}
                </span>
              )}
            </div>

            {editTarget?.kind === 'legacy' ? (
              <FormulaEditor
                oils={oils}
                inventory={inventoryOils}
                tiered={editorTiered}
                onUpdate={updateOil}
                onRemove={removeOil}
                onAdd={addOil}
                onSave={handleSaveFormula}
                onCancel={cancelEditFormula}
                saving={savingFormula}
                valid={formulaValid}
              />
            ) : (order.formulation_details.oils ?? []).length === 0 ? (
              <p className="text-sm text-sub">
                No oil formula recorded. Use “Edit formula” to add one.
              </p>
            ) : (
              <FormulaView
                oils={order.formulation_details.oils ?? []}
                grouped={order.formulation_details.formula_style !== 'flat'}
                codes={oilCodes}
              />
            )}

            {/* Edit audit trail — who changed the formula and when */}
            {editTarget?.kind !== 'legacy' && (order.formulation_details.edit_history ?? []).length > 0 && (
              <div className="mt-4 border-t border-edge pt-3">
                <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-sub">
                  Edit history
                </h4>
                <ul className="space-y-1.5 text-xs text-sub">
                  {(order.formulation_details.edit_history ?? []).map((h, i) => (
                    <li key={`${h.changed_at}-${i}`} className="flex flex-wrap gap-x-2">
                      <span className="font-medium text-ink">{h.changed_by}</span>
                      <span>· {formatDateTime(h.changed_at)}</span>
                      {h.summary && <span>· {h.summary}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Modern custom perfume formulas — one oil recipe per custom line item.
          Resolved from the AI service; admin edits are saved as a per-item override. */}
      {(order.custom_formulas ?? []).length > 0 && showFormula && (
        <Card title="Custom perfume formula">
          <div className="space-y-6">
            {(order.custom_formulas ?? []).map((cf) => {
              const editing =
                editTarget?.kind === 'custom' && editTarget.itemId === cf.order_item_id;
              const total = cf.oils.reduce((s, o) => s + (Number(o.ml) || 0), 0);
              return (
                <div
                  key={cf.order_item_id}
                  className="rounded-xl border border-edge p-4 first:pt-4"
                >
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <h3 className="text-sm font-semibold">{cf.product_name}</h3>
                      <p className="mt-0.5 text-xs text-sub">
                        {cf.overridden ? (
                          <span className="text-gold">Admin-edited formula</span>
                        ) : (
                          'Original formula'
                        )}
                        {!editing && ` · Total ${formatMl(total)}`}
                      </p>
                    </div>
                    {!editing && (
                      <button className="btn-ghost text-sm" onClick={() => startEditCustom(cf)}>
                        Edit formula
                      </button>
                    )}
                  </div>

                  {editing ? (
                    <FormulaEditor
                      oils={oils}
                      inventory={inventoryOils}
                      tiered={editorTiered}
                      onUpdate={updateOil}
                      onRemove={removeOil}
                      onAdd={addOil}
                      onSave={handleSaveFormula}
                      onCancel={cancelEditFormula}
                      saving={savingFormula}
                      valid={formulaValid}
                    />
                  ) : cf.oils.length === 0 ? (
                    <p className="text-sm text-sub">
                      No oil formula could be resolved for this item (the AI service may be
                      unavailable). Use “Edit formula” to enter one manually.
                    </p>
                  ) : (
                    <FormulaView oils={cf.oils} grouped codes={oilCodes} />
                  )}

                  {!editing && cf.overridden && cf.edited_by && (
                    <p className="mt-3 text-xs text-sub">
                      Last edited by <span className="font-medium text-ink">{cf.edited_by}</span>
                      {cf.edited_at && ` · ${formatDateTime(cf.edited_at)}`}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Formulation cost — internal raw-material costing, never shown to customers.
          Costed FIFO server-side: each ml is charged at the price of the batch it
          came from, so a restock at a new price doesn't re-price older stock.
          Collapsed to the total by default; the per-oil breakdown is on demand.
          Gated with the recipe panels above: its breakdown lists the same oils
          and ml, so leaving it open would defeat hiding them. */}
      {formulationCost && formulationOils.length > 0 && showFormula && (
        <Card
          title="Formulation Cost"
          action={
            <button
              type="button"
              className="btn-ghost text-sm"
              onClick={() => setCostOpen((v) => !v)}
              aria-expanded={costOpen}
            >
              {costOpen ? 'Hide breakdown' : 'Show breakdown'}
              <IconChevronRight
                className={`h-4 w-4 transition-transform ${costOpen ? 'rotate-90' : ''}`}
              />
            </button>
          }
        >
          {!formulationCost.isComplete && (
            <div className="mb-4 flex items-start gap-3 rounded-xl border border-warn/40 bg-warn/10 px-4 py-3 text-sm text-warn">
              <IconAlert className="mt-0.5 h-4 w-4 shrink-0" />
              <p>
                <span className="font-semibold">Incomplete costing.</span> No cost set for{' '}
                {formulationCost.missingPricing.join(', ')}. Add pricing on the{' '}
                <Link to="/inventory" className="font-medium underline">
                  Inventory page
                </Link>{' '}
                for an accurate total.
              </p>
            </div>
          )}
          {/* Some ml fell outside the recorded batches, so part of the total is a
              projection at the latest price rather than a price actually paid. */}
          {formulationCost.hasEstimatedCost && (
            <div className="mb-4 flex items-start gap-3 rounded-xl border border-warn/40 bg-warn/10 px-4 py-3 text-sm text-warn">
              <IconAlert className="mt-0.5 h-4 w-4 shrink-0" />
              <p>
                <span className="font-semibold">Partly estimated.</span> Some oils need more ml
                than their recorded cost batches cover — the shortfall is priced at the most
                recent rate known for that oil. Record restocks with their cost on the{' '}
                <Link to="/inventory" className="font-medium underline">
                  Inventory page
                </Link>{' '}
                to keep this exact.
              </p>
            </div>
          )}
          {/* A packaging item with no unit cost is silently costed at ₹0, which
              reads as "free packaging" rather than "we never entered a price". */}
          {packaging && packaging.unpriced.length > 0 && (
            <div className="mb-4 flex items-start gap-3 rounded-xl border border-warn/40 bg-warn/10 px-4 py-3 text-sm text-warn">
              <IconAlert className="mt-0.5 h-4 w-4 shrink-0" />
              <p>
                <span className="font-semibold">Packaging partly unpriced.</span> No unit cost set
                for {packaging.unpriced.join(', ')}. Record it on the{' '}
                <Link to="/packaging" className="font-medium underline">
                  Packaging page
                </Link>{' '}
                so the landed cost isn't understated.
              </p>
            </div>
          )}
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-sm text-sub">
              Total cost{packaging && packaging.items.length > 0 ? ' — oils + packaging' : ''}
            </span>
            <span className="text-lg font-semibold">{formatINRPrecise(formulationCost.total)}</span>
          </div>

          {costOpen && (
            <div className="mt-4 overflow-x-auto border-t border-edge pt-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-edge">
                    <th className="th">Oil</th>
                    <th className="th text-right">ml</th>
                    <th className="th text-right">Cost/ml</th>
                    <th className="th text-right">Line cost</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-edge">
                  {formulationCost.breakdown.map((line, i) => (
                    <tr key={`${line.name}-${i}`}>
                      <td className="td font-medium">
                        <OilCodeTag code={oilCodes.get(line.name)} />
                        {line.name}
                      </td>
                      <td className="td text-right text-sub">{formatMl(line.ml)}</td>
                      <td className="td text-right">
                        {line.hasPricing ? (
                          <>
                            {formatINRPrecise(line.unit_cost_per_ml)}
                            {/* Drawn from more than one batch — the rate above is
                                the blend, so say so rather than let it read as a
                                single price we paid. */}
                            {line.layers.length > 1 && (
                              <span className="block text-xs text-sub">
                                blended · {line.layers.length} batches
                              </span>
                            )}
                          </>
                        ) : (
                          <span className="text-warn">not set</span>
                        )}
                      </td>
                      <td className="td text-right font-medium">
                        {line.hasPricing ? formatINRPrecise(line.line_cost) : <span className="text-sub">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Packaging — one complete set per parcel, whatever the order's
                  quantity: every item ships individually in its own box. Split
                  orders that went out as two shipments consumed two sets. */}
              {packaging && packaging.items.length > 0 && (
                <div className="mt-6 border-t border-edge pt-4">
                  <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
                    <p className="text-xs font-medium uppercase tracking-wide text-sub">Packaging</p>
                    <p className="text-xs text-sub">
                      {packaging.projected
                        ? 'Projected — 1 set for the parcel this order will ship in'
                        : `${packaging.sets} set${packaging.sets === 1 ? '' : 's'} — one per parcel shipped`}
                    </p>
                  </div>
                  <table className="w-full text-sm">
                    <tbody className="divide-y divide-edge">
                      {packaging.items.map((line) => (
                        <tr key={line.item_name}>
                          <td className="td font-medium">{line.item_name}</td>
                          <td className="td text-right text-sub">×{line.units}</td>
                          <td className="td text-right font-medium">
                            {line.unit_cost > 0 ? (
                              formatINRPrecise(line.line_cost)
                            ) : (
                              <span className="text-warn">no cost set</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Cost of goods — oils + packaging, untaxed. GST is a customer
                  price concept and has no place in an internal cost view. */}
              <table className="mt-4 w-full text-sm">
                <tbody>
                  <tr>
                    <td className="td text-right text-sub">Oils subtotal</td>
                    <td className="td w-32 text-right">{formatINRPrecise(formulationCost.subtotal)}</td>
                  </tr>
                  {packaging && packaging.items.length > 0 && (
                    <tr>
                      <td className="td text-right text-sub">Packaging subtotal</td>
                      <td className="td w-32 text-right">{formatINRPrecise(packaging.subtotal)}</td>
                    </tr>
                  )}
                  <tr className="border-t border-edge">
                    <td className="td text-right font-semibold">Total cost</td>
                    <td className="td w-32 text-right font-semibold">
                      {formatINRPrecise(formulationCost.total)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* Fulfilment — Formulated stage, then Shiprocket multi-shipment shipping */}
      <Card title="Shipping">
        <div className="space-y-4">
          {/* GST invoice — available from the moment payment lands and for the
              rest of the order's life, so a reprint is never more than a click
              away regardless of how far the parcel has travelled. */}
          {canDownloadInvoice && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-edge p-3">
              <div className="text-sm">
                <p className="font-medium">GST invoice</p>
                <p className="text-xs text-sub">4×6 inch — prints on label stock.</p>
              </div>
              <button
                className="btn-outline !py-1.5 !text-xs"
                onClick={handleDownloadInvoice}
                disabled={downloadingInvoice}
              >
                {downloadingInvoice ? <Spinner /> : <IconDownload className="h-3.5 w-3.5" />}
                Download Invoice
              </button>
            </div>
          )}

          {/* Mark as Formulated — the perfume is compounded and bottled. Creates
              the Shiprocket order only; no courier, no AWB, no notification. */}
          {canMarkFormulated && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-edge p-3">
              <p className="text-sm text-sub">
                Perfume compounded and bottled? Mark it Formulated to register the order with
                Shiprocket. No courier is assigned and the customer is not notified.
              </p>
              <button
                className="btn-gold"
                onClick={handleMarkFormulated}
                disabled={markingFormulated}
              >
                {markingFormulated && <Spinner />}
                Mark as Formulated
              </button>
            </div>
          )}

          {/* Self-ship — the exception path for hand-delivered orders. Deliberately
              an outline button: Shiprocket remains the default. */}
          {canSelfShip && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-dashed border-edge p-3">
              <p className="text-sm text-sub">
                Delivering this by hand? Skip Shiprocket and mark it delivered directly.
              </p>
              <button className="btn-outline" onClick={() => setConfirmSelfShip(true)}>
                Self-ship (skip Shiprocket)
              </button>
            </div>
          )}

          {isSelfShipped && (
            <div className="flex items-center gap-2 rounded-xl border border-edge px-4 py-3 text-sm text-sub">
              <IconCheck className="h-4 w-4 shrink-0" />
              <span>
                <span className="font-semibold text-ink">Self-shipped</span> — delivered by hand, no
                Shiprocket shipment.
              </span>
            </div>
          )}

          {isFormulated && (
            <div className="space-y-2 rounded-xl border border-edge p-3">
              <div className="flex items-center gap-2">
                <StatusBadge status="formulated" />
                <span className="text-sm text-sub">Bottled and awaiting a courier.</span>
              </div>
              {shiprocketOrderId ? (
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="text-sub">Shiprocket Order ID</span>
                  <span className="font-medium">{shiprocketOrderId}</span>
                  <button
                    className="btn-ghost !px-1.5 !py-0.5 text-xs"
                    onClick={() => handleCopy(shiprocketOrderId)}
                  >
                    {copied === shiprocketOrderId ? 'Copied' : 'Copy'}
                  </button>
                </div>
              ) : (
                <p className="flex items-start gap-2 text-sm text-warn">
                  <IconAlert className="mt-0.5 h-4 w-4 shrink-0" />
                  Shiprocket order not yet created. Ready to Ship will create it first.
                </p>
              )}
            </div>
          )}

          {isRto && (
            <div className="flex items-start gap-3 rounded-xl border border-bad/40 bg-bad/10 px-4 py-3 text-sm text-bad">
              <IconAlert className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                <p className="font-semibold">
                  {shippingStatus === 'rto_delivered'
                    ? 'Returned to origin'
                    : 'Return to origin initiated'}
                </p>
                <p className="mt-0.5">Contact customer to arrange re-delivery or refund.</p>
              </div>
            </div>
          )}
          {isDelivered && (
            <div className="flex items-center gap-2 rounded-xl border border-ok/40 bg-ok/10 px-4 py-3 text-sm text-ok">
              <IconCheck className="h-4 w-4 shrink-0" />
              <span className="font-semibold">Delivered</span>
            </div>
          )}

          {/* Existing shipments (an order may be split into several) */}
          {shipments.length > 0 && (
            <div className="space-y-3">
              {/* Combined label for a split order — one PDF for all its parcels */}
              {shipments.filter((s) => s.awb_number && s.shiprocket_shipment_id).length >= 2 && (
                <div className="flex justify-end">
                  <button
                    className="btn-outline !py-1.5 !text-xs"
                    onClick={handleDownloadAllLabels}
                    disabled={downloadingLabels}
                  >
                    {downloadingLabels && <Spinner />}
                    Download all labels
                  </button>
                </div>
              )}
              {shipments.map((sh) => {
                // Where this parcel actually got to. Anything short of a label is
                // a half-state: show what happened and how to finish it, rather
                // than a blank AWB row that reads as "not shipped yet".
                const step = shipmentState(sh);
                const settled = step === 'complete' || step === 'label_generated';
                const retrying = retryingShipment === sh.id;
                // Registered with Shiprocket and waiting for the admin to press
                // Ready to Ship — the resting state of a formulated order, not a
                // stall, so it gets a calm note instead of an alarm panel.
                const awaitingCourier = parkedAtFormulated(sh);

                return (
                  <div key={sh.id} className="space-y-2 rounded-xl border border-edge p-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-semibold">Shipment #{sh.seq}</span>
                      <ShippingBadge status={sh.shipping_status} />
                    </div>
                    {sh.items.length > 0 && (
                      <p className="text-xs text-sub">
                        {sh.items.map((it) => `${it.product_name} ×${it.units}`).join(', ')}
                      </p>
                    )}

                    {settled ? (
                      <>
                        <dl className="space-y-1 text-sm">
                          <div className="flex justify-between gap-3">
                            <dt className="text-sub">AWB</dt>
                            <dd className="flex items-center gap-2 font-medium">
                              {sh.awb_number ?? '—'}
                              {sh.awb_number && (
                                <button
                                  className="btn-ghost !px-1.5 !py-0.5 text-xs"
                                  onClick={() => handleCopy(sh.awb_number!)}
                                >
                                  {copied === sh.awb_number ? 'Copied' : 'Copy'}
                                </button>
                              )}
                            </dd>
                          </div>
                          <div className="flex justify-between gap-3">
                            <dt className="text-sub">Courier</dt>
                            <dd className="font-medium">{sh.courier_name ?? '—'}</dd>
                          </div>
                          {sh.pickup_scheduled_at && (
                            <div className="flex justify-between gap-3">
                              <dt className="text-sub">Pickup scheduled</dt>
                              <dd className="font-medium">{formatDateTime(sh.pickup_scheduled_at)}</dd>
                            </div>
                          )}
                        </dl>
                        <div className="flex flex-wrap items-center gap-2">
                          {sh.shipping_status === 'ready_to_ship' &&
                            (pickupFor === sh.id ? (
                              <div className="flex flex-wrap items-center gap-2">
                                <input
                                  type="date"
                                  className="input !w-auto !py-1 !text-xs"
                                  min={new Date().toISOString().slice(0, 10)}
                                  value={pickupDate}
                                  onChange={(e) => setPickupDate(e.target.value)}
                                />
                                <button
                                  className="btn-gold !py-1.5 !text-xs"
                                  onClick={() => handleSchedulePickup(sh.id, pickupDate)}
                                  disabled={schedulingPickup === sh.id}
                                >
                                  {schedulingPickup === sh.id && <Spinner />}
                                  Confirm pickup
                                </button>
                                <button
                                  className="btn-ghost !py-1.5 !text-xs"
                                  onClick={() => setPickupFor(null)}
                                  disabled={schedulingPickup === sh.id}
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <button
                                className="btn-gold !py-1.5 !text-xs"
                                onClick={() => {
                                  setPickupFor(sh.id);
                                  // default to tomorrow
                                  setPickupDate(new Date(Date.now() + 86400000).toISOString().slice(0, 10));
                                }}
                              >
                                <IconTruck className="h-3.5 w-3.5" />
                                {sh.pickup_scheduled_at ? 'Re-schedule Pickup' : 'Schedule Pickup'}
                              </button>
                            ))}
                          {sh.label_url && (
                            <a
                              className="btn-outline !py-1.5 !text-xs"
                              href={sh.label_url}
                              target="_blank"
                              rel="noreferrer"
                            >
                              Label <IconExternal className="h-3 w-3" />
                            </a>
                          )}
                        </div>
                      </>
                    ) : awaitingCourier ? (
                      <div className="space-y-1 text-sm">
                        <p className="text-sub">
                          Registered with Shiprocket. A courier is assigned when you mark this
                          order Ready to Ship.
                        </p>
                        {sh.shiprocket_order_id && (
                          <CopyableId
                            label="Shiprocket Order ID"
                            value={sh.shiprocket_order_id}
                            copied={copied}
                            onCopy={handleCopy}
                          />
                        )}
                      </div>
                    ) : (
                      <HalfStatePanel
                        step={step}
                        shipment={sh}
                        copied={copied}
                        onCopy={handleCopy}
                        onRetry={() => handleRetryShipment(sh.id)}
                        onSync={handleSyncShipment}
                        retrying={retrying}
                        syncing={syncing}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Create a (further) shipment while items remain to ship */}
          {canShip ? (
            <div className="space-y-2">
              {/* A formulated order's parcel is already open — pressing this
                  couriers THAT parcel rather than creating a second one, so it
                  keeps the "Mark Ready to Ship" name. */}
              {shipments.length > 0 && !shipments.every(parkedAtFormulated) && (
                <p className="text-xs text-sub">{remainingToShip} item(s) still to ship.</p>
              )}
              <button className="btn-gold" onClick={() => setShipmentOpen(true)}>
                <IconTruck className="h-4 w-4" />
                {shipments.length > 0 && !shipments.every(parkedAtFormulated)
                  ? 'Create Another Shipment'
                  : 'Mark Ready to Ship'}
              </button>
            </div>
          ) : shipments.length === 0 ? (
            <p className="text-sm text-sub">
              {awaitingPayment
                ? 'Waiting for payment before this order can be shipped.'
                : !paymentReceived
                ? 'Payment has not been received yet — shipping becomes available once payment is confirmed.'
                : 'This order cannot be shipped in its current state.'}
            </p>
          ) : null}
        </div>
      </Card>

      {/* Cancel order */}
      {canCancel && (
        <Card title="Cancel order">
          <div className="space-y-4">
            <div>
              <label className="label">Reason</label>
              <textarea
                className="input min-h-[72px]"
                placeholder="Why is this order being cancelled?"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
              />
            </div>
            <button
              className="btn-danger"
              onClick={() => setConfirmCancel(true)}
              disabled={!cancelReason.trim()}
            >
              Cancel order
            </button>
          </div>
        </Card>
      )}

      {/* Status history */}
      <Card title="Status history">
        {(order.status_history ?? []).length === 0 ? (
          <p className="text-sm text-sub">No status changes recorded</p>
        ) : (
          <ol className="relative space-y-6 border-l border-edge pl-6">
            {[...order.status_history]
              .sort((a, b) => new Date(b.changed_at).getTime() - new Date(a.changed_at).getTime())
              .map((h, i) => (
                <li key={`${h.status}-${h.changed_at}-${i}`} className="relative">
                  <span
                    className={`absolute -left-[30px] top-1 h-2.5 w-2.5 rounded-full ${
                      i === 0 ? 'bg-gold' : 'bg-edge'
                    }`}
                  />
                  <div className="flex flex-wrap items-center gap-2">
                    <StatusBadge status={h.status} />
                    <span className="text-xs text-sub">{formatDateTime(h.changed_at)}</span>
                    {h.changed_by && <span className="text-xs text-sub">by {h.changed_by}</span>}
                  </div>
                  {h.notes && <p className="mt-1 text-sm text-sub">{h.notes}</p>}
                </li>
              ))}
          </ol>
        )}
      </Card>

      <ConfirmDialog
        open={confirmCancel}
        title="Cancel this order?"
        message={
          <>
            Order {shortId(order.id)} will be cancelled with reason: “{cancelReason.trim()}”. This
            cannot be undone.
          </>
        }
        confirmLabel="Cancel order"
        danger
        busy={cancelling}
        onConfirm={handleCancel}
        onClose={() => setConfirmCancel(false)}
      />

      <ConfirmDialog
        open={confirmSelfShip}
        title="Mark as self-shipped?"
        message={
          <div className="space-y-3">
            <p>
              This skips Shiprocket entirely — no AWB, no tracking, no courier label. The order goes
              straight to Delivered and the customer gets the delivered message.
            </p>
            <p>Use this only for orders you are delivering by hand. This cannot be undone.</p>
            <div>
              <label className="label">Delivery note (optional)</label>
              <textarea
                className="input min-h-[64px]"
                placeholder="Self-shipped — delivered by hand"
                value={selfShipNote}
                onChange={(e) => setSelfShipNote(e.target.value)}
                disabled={selfShipping}
              />
            </div>
          </div>
        }
        confirmLabel="Mark Delivered"
        busy={selfShipping}
        onConfirm={handleSelfShip}
        onClose={() => {
          setConfirmSelfShip(false);
          setSelfShipNote('');
        }}
      />

      <ShipmentModal
        open={shipmentOpen}
        order={order}
        onClose={() => setShipmentOpen(false)}
        onShipped={load}
      />
    </div>
  );
}

/* ---------- Half-created shipment recovery ---------- */

// Human wording for a shipment step, used in the retry confirmation line.
const STEP_LABELS: Record<ShipmentStep, string> = {
  order_created: 'Shiprocket order created',
  courier_assigned: 'Courier assigned',
  label_generated: 'Label generated',
  complete: 'Complete',
  failed: 'Failed',
};

/**
 * Where a parcel actually got to. The backend derives a step for shipments that
 * predate the column, so this fallback only matters for a row interrupted before
 * it could record anything — which is, by definition, a failure.
 */
function shipmentState(sh: OrderShipment): ShipmentStep {
  if (sh.shipment_step) return sh.shipment_step;
  if (sh.awb_number && sh.label_url) return 'complete';
  if (sh.awb_number) return 'courier_assigned';
  if (sh.shiprocket_order_id) return 'order_created';
  return 'failed';
}

/** An id/AWB the admin will want to paste into the Shiprocket dashboard. */
function CopyableId({
  label,
  value,
  copied,
  onCopy,
}: {
  label: string;
  value: string;
  copied: string;
  onCopy: (v: string) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <span className="text-sub">{label}:</span>
      <span className="font-mono font-medium">{value}</span>
      <button className="btn-ghost !px-1.5 !py-0.5 text-xs" onClick={() => onCopy(value)}>
        {copied === value ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
}

/**
 * The Shipping panel for a parcel that didn't make it all the way through
 * Shiprocket. Names the exact step it stopped at, shows the ids needed to find it
 * in the Shiprocket dashboard, and offers the two ways out: retry (resumes from
 * the failed step) or sync (adopt whatever the admin fixed by hand over there).
 */
function HalfStatePanel({
  step,
  shipment,
  copied,
  onCopy,
  onRetry,
  onSync,
  retrying,
  syncing,
}: {
  step: ShipmentStep;
  shipment: OrderShipment;
  copied: string;
  onCopy: (v: string) => void;
  onRetry: () => void;
  onSync: () => void;
  retrying: boolean;
  syncing: boolean;
}) {
  const failed = step === 'failed';
  const tone = failed
    ? 'border-bad/40 bg-bad/10 text-bad'
    : 'border-warn/40 bg-warn/10 text-warn';

  const heading = failed
    ? 'Shipment creation failed'
    : step === 'order_created'
    ? 'Shipment partially created'
    : 'Courier assigned, label pending';

  const detail = failed
    ? null
    : step === 'order_created'
    ? 'Shiprocket order created but courier not assigned.'
    : 'The AWB is assigned and billed — only the label PDF is missing.';

  const retryLabel = failed
    ? 'Retry from Beginning'
    : step === 'order_created'
    ? 'Retry Courier Assignment'
    : 'Retry Label Generation';

  return (
    <div className={`space-y-3 rounded-xl border px-4 py-3 ${tone}`}>
      <div className="flex items-start gap-3">
        <IconAlert className="mt-0.5 h-4 w-4 shrink-0" />
        <div className="space-y-1">
          <p className="text-sm font-semibold">{heading}</p>
          {detail && <p className="text-sm">{detail}</p>}
          {shipment.shipment_error && (
            <p className="text-sm">
              <span className="font-medium">Error:</span> {shipment.shipment_error}
            </p>
          )}
        </div>
      </div>

      <div className="space-y-1 text-text">
        {shipment.awb_number && (
          <CopyableId label="AWB" value={shipment.awb_number} copied={copied} onCopy={onCopy} />
        )}
        {shipment.shiprocket_order_id && (
          <CopyableId
            label="Shiprocket Order ID"
            value={shipment.shiprocket_order_id}
            copied={copied}
            onCopy={onCopy}
          />
        )}
      </div>

      {failed && shipment.shiprocket_order_id && (
        <p className="text-xs text-sub">
          This order exists in Shiprocket. Fix the issue there and use Sync to pull the latest
          state.
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <button
          className="btn-gold !py-1.5 !text-xs"
          onClick={onRetry}
          disabled={retrying || syncing}
        >
          {retrying && <Spinner />}
          {retryLabel} ▸
        </button>
        <button
          className="btn-outline !py-1.5 !text-xs"
          onClick={onSync}
          disabled={retrying || syncing}
        >
          {syncing && <Spinner />}
          Sync from Shiprocket ▸
        </button>
      </div>
    </div>
  );
}

/* ---------- Shipping status badge ---------- */

// Shiprocket shipping_status → color family. Distinct from the order StatusBadge
// (which maps order-lifecycle statuses); meaning is never color-alone.
const SHIPPING_STYLES: Record<string, string> = {
  created:
    'bg-slate-500/10 text-slate-600 border-slate-500/30 dark:bg-slate-400/10 dark:text-slate-300 dark:border-slate-400/30',
  cancelled:
    'bg-red-500/10 text-red-600 border-red-500/30 dark:bg-red-400/10 dark:text-red-300 dark:border-red-400/30',
  ready_to_ship:
    'bg-amber-500/10 text-amber-600 border-amber-500/30 dark:bg-amber-400/10 dark:text-amber-300 dark:border-amber-400/30',
  shipped:
    'bg-indigo-500/10 text-indigo-600 border-indigo-500/30 dark:bg-indigo-400/10 dark:text-indigo-300 dark:border-indigo-400/30',
  delivered:
    'bg-green-500/10 text-green-600 border-green-500/30 dark:bg-green-400/10 dark:text-green-300 dark:border-green-400/30',
  rto_initiated:
    'bg-red-500/10 text-red-600 border-red-500/30 dark:bg-red-400/10 dark:text-red-300 dark:border-red-400/30',
  rto_delivered:
    'bg-red-500/10 text-red-600 border-red-500/30 dark:bg-red-400/10 dark:text-red-300 dark:border-red-400/30',
};

function ShippingBadge({ status }: { status: string | null }) {
  const key = status ?? 'ready_to_ship';
  const cls = SHIPPING_STYLES[key] ?? SHIPPING_STYLES.ready_to_ship;
  const label = (status ?? 'ready_to_ship')
    .replace(/_/g, ' ')
    .replace(/^\w/, (c) => c.toUpperCase());
  return (
    <span
      className={`inline-flex items-center whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-medium ${cls}`}
    >
      {label}
    </span>
  );
}

/* ---------- Oil formula helpers ---------- */

const TIER_OPTIONS: { value: Exclude<OilTier, ''>; label: string }[] = [
  { value: 'top', label: 'Top' },
  { value: 'heart', label: 'Heart' },
  { value: 'base', label: 'Base' },
];

// Display order + headings for grouping oils by note.
const TIER_VIEW_ORDER: OilTier[] = ['top', 'heart', 'base', ''];
const TIER_HEADING: Record<string, string> = {
  top: 'Top notes',
  heart: 'Heart notes',
  base: 'Base notes',
  '': 'Other',
};

// ml display and rounding both come from utils/format (formatMl / round1) — one
// decimal, always. The local 3 dp formatter this replaced showed "2.5 ml" for a
// value the services had already fixed at 2.5 and "0.30000000000000004 ml" for
// one they hadn't, which made a rounding bug look like a display quirk.

// Read-only oil list. When `grouped`, oils are split into Top / Heart / Base
// note sections (each with its ml subtotal); otherwise a single flat table.
function FormulaView({
  oils,
  grouped,
  codes,
}: {
  oils: FormulaOil[];
  grouped?: boolean;
  /** oil name -> shelf number, so the bench can find the bottle it names. */
  codes: Map<string, number>;
}) {
  const flatTable = (rows: FormulaOil[]) => (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <tbody className="divide-y divide-edge">
          {rows.map((o, i) => (
            <tr key={`${o.name}-${i}`}>
              <td className="td font-medium">
                <OilCodeTag code={codes.get(o.name)} />
                {o.name}
              </td>
              <td className="td text-right font-medium">{formatMl(o.ml)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  if (!grouped) return flatTable(oils);

  const groups = TIER_VIEW_ORDER.map((tier) => ({
    tier,
    items: oils.filter((o) => (o.tier || '') === tier),
  })).filter((g) => g.items.length > 0);

  return (
    <div className="space-y-4">
      {groups.map((g) => {
        const subtotal = g.items.reduce((s, o) => s + (Number(o.ml) || 0), 0);
        return (
          <div key={g.tier || 'other'}>
            <div className="mb-1 flex items-center justify-between">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-sub">
                {TIER_HEADING[g.tier || '']}
              </h4>
              <span className="text-xs text-sub">{formatMl(subtotal)}</span>
            </div>
            {flatTable(g.items)}
          </div>
        );
      })}
    </div>
  );
}

function FormulaEditor({
  oils,
  inventory,
  tiered,
  onUpdate,
  onRemove,
  onAdd,
  onSave,
  onCancel,
  saving,
  valid,
}: {
  oils: FormulaOil[];
  /** Live inventory, so oil names can be picked canonically and checked. */
  inventory: InventoryItem[];
  tiered: boolean;
  onUpdate: (i: number, patch: Partial<FormulaOil>) => void;
  onRemove: (i: number) => void;
  onAdd: () => void;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
  valid: boolean;
}) {
  const total = oils.reduce((s, o) => s + (Number(o.ml) || 0), 0);

  // Raw text of the ml field while it's being typed. A controlled number bound
  // straight to `o.ml` swallows the decimal point ("2." parses to 2 and renders
  // back as "2"), so keystrokes are held here and only committed as a number.
  const [mlDrafts, setMlDrafts] = useState<Record<number, string>>({});
  const clearDrafts = () => setMlDrafts({});

  // Derived here rather than passed in: the editor already holds the inventory
  // list the numbers come from.
  const codes = useMemo(() => oilCodeMap(inventory), [inventory]);

  const handleMlChange = (i: number, raw: string) => {
    // Digits with at most one decimal place — anything else is rejected outright
    // rather than silently rounded, so what's on screen is what gets saved.
    if (raw !== '' && !/^\d*\.?\d?$/.test(raw)) return;
    setMlDrafts((prev) => ({ ...prev, [i]: raw }));
    onUpdate(i, { ml: round1(parseFloat(raw)) });
  };

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        {oils.map((o, i) => (
          <div key={i} className="flex items-start gap-2">
            {/* Fixed-width gutter, not an inline tag: the numbers stay in a
                readable column down the rows, and a nameless new row keeps its
                input aligned with the ones above it. */}
            <span className="w-8 shrink-0 pt-2 text-right leading-none">
              <OilCodeTag code={codes.get(o.name.trim())} />
            </span>
            {/* Picking from the list writes inventory's exact oil_name. A name
                that doesn't match one flags itself in red — it will not deduct. */}
            <OilPicker
              className="flex-1"
              value={o.name}
              oils={inventory}
              onChange={(name) => onUpdate(i, { name })}
            />
            {tiered && (
              <select
                className="input w-28 shrink-0"
                value={o.tier || 'heart'}
                onChange={(e) => onUpdate(i, { tier: e.target.value as OilTier })}
              >
                {TIER_OPTIONS.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            )}
            <input
              className="input w-24 shrink-0"
              type="text"
              inputMode="decimal"
              placeholder="ml"
              aria-label="ml"
              value={mlDrafts[i] ?? (o.ml === 0 ? '' : String(o.ml))}
              onChange={(e) => handleMlChange(i, e.target.value)}
              onBlur={clearDrafts}
            />
            <button
              type="button"
              className="btn-ghost shrink-0 px-2 text-bad hover:text-bad"
              onClick={() => {
                clearDrafts(); // indices shift on removal — stale drafts would land on the wrong row
                onRemove(i);
              }}
              aria-label="Remove oil"
            >
              ✕
            </button>
          </div>
        ))}
      </div>

      <button
        type="button"
        className="btn-ghost text-sm"
        onClick={() => {
          clearDrafts();
          onAdd();
        }}
      >
        + Add oil
      </button>

      <div className="flex items-center justify-between border-t border-edge pt-3">
        <span className="text-xs text-sub">Total {formatMl(total)}</span>
        <div className="flex items-center gap-2">
          <button className="btn-ghost" onClick={onCancel} disabled={saving}>
            Cancel
          </button>
          <button className="btn-gold" onClick={onSave} disabled={saving || !valid}>
            {saving && <Spinner />}
            Save formula
          </button>
        </div>
      </div>
    </div>
  );
}
